import React, { useState, useEffect } from 'react';
import { Users as UsersIcon, Plus, Edit2, Trash2, Lock, Mail, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // フォームデータ
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'editor'
  });

  // ユーザー一覧を取得
  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('ユーザー取得エラー:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // 通知を表示
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  // 新規ユーザー作成
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showNotification('ユーザーを作成しました', 'success');
        setShowCreateModal(false);
        setFormData({ username: '', email: '', password: '', role: 'editor' });
        fetchUsers();
      } else {
        const error = await response.json();
        showNotification(error.detail || 'ユーザー作成に失敗しました', 'error');
      }
    } catch (error) {
      showNotification('エラーが発生しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ユーザー更新
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {};
      if (formData.email) updateData.email = formData.email;
      if (formData.password) updateData.password = formData.password;
      if (formData.role) updateData.role = formData.role;

      const response = await fetch(`${API_URL}/api/users/${selectedUser.username}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        showNotification('ユーザー情報を更新しました', 'success');
        setShowEditModal(false);
        setSelectedUser(null);
        setFormData({ username: '', email: '', password: '', role: 'editor' });
        fetchUsers();
      } else {
        const error = await response.json();
        showNotification(error.detail || '更新に失敗しました', 'error');
      }
    } catch (error) {
      showNotification('エラーが発生しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ユーザー削除
  const handleDeleteUser = async (username) => {
    if (!window.confirm(`ユーザー「${username}」を削除しますか？`)) return;

    try {
      const response = await fetch(`${API_URL}/api/users/${username}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        showNotification('ユーザーを削除しました', 'success');
        fetchUsers();
      } else {
        const error = await response.json();
        showNotification(error.detail || '削除に失敗しました', 'error');
      }
    } catch (error) {
      showNotification('エラーが発生しました', 'error');
    }
  };

  // 編集モーダルを開く
  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role
    });
    setShowEditModal(true);
  };

  // 権限のラベル
  const getRoleLabel = (role) => {
    const roles = {
      admin: '管理者',
      editor: '編集者',
      viewer: '閲覧者'
    };
    return roles[role] || role;
  };

  // 権限の色
  const getRoleColor = (role) => {
    const colors = {
      admin: 'text-red-400',
      editor: 'text-amber-500',
      viewer: 'text-gray-600'
    };
    return colors[role] || 'text-gray-600';
  };

  return (
    <div className="p-6">
      {/* 通知 */}
      {notification.show && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-gray-900`}>
          {notification.message}
        </div>
      )}

      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-amber-500 flex items-center gap-2">
          <UsersIcon className="w-6 h-6" />
          ユーザー管理
        </h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 rounded-lg transition w-full sm:w-auto"
        >
          <Plus className="w-5 h-5" />
          新規ユーザー
        </button>
      </div>

      {/* ユーザー一覧 */}
      <div className="bg-white/50 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/50">
            <tr>
              <th className="px-6 py-3 text-left text-amber-500">ユーザー名</th>
              <th className="px-6 py-3 text-left text-amber-500">メールアドレス</th>
              <th className="px-6 py-3 text-left text-amber-500">権限</th>
              <th className="px-6 py-3 text-left text-amber-500">作成日</th>
              <th className="px-6 py-3 text-right text-amber-500">操作</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.username} className="border-t border-gray-200 hover:bg-gray-100/30">
                <td className="px-6 py-4">{user.username}</td>
                <td className="px-6 py-4">{user.email}</td>
                <td className="px-6 py-4">
                  <span className={`flex items-center gap-2 ${getRoleColor(user.role)}`}>
                    <Shield className="w-4 h-4" />
                    {getRoleLabel(user.role)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {new Date(user.created_at).toLocaleDateString('ja-JP')}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => openEditModal(user)}
                      className="p-2 hover:bg-amber-500/20 rounded-lg transition"
                      title="編集"
                    >
                      <Edit2 className="w-4 h-4 text-amber-500" />
                    </button>
                    {user.username !== 'admin' && (
                      <button
                        onClick={() => handleDeleteUser(user.username)}
                        className="p-2 hover:bg-red-500/20 rounded-lg transition"
                        title="削除"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="text-center py-12 text-gray-600">
            ユーザーが登録されていません
          </div>
        )}
      </div>

      {/* 新規作成モーダル */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-white/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-amber-500 mb-4">新規ユーザー作成</h3>
            <form onSubmit={handleCreateUser}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">ユーザー名</label>
                  <input
                    type="text"
                    required
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg px-4 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    メールアドレス
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg px-4 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1 flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    パスワード
                  </label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg px-4 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    権限
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg px-4 py-2"
                  >
                    <option value="admin">管理者</option>
                    <option value="editor">編集者</option>
                    <option value="viewer">閲覧者</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 px-4 py-2 rounded-lg transition disabled:opacity-50"
                >
                  {loading ? '作成中...' : '作成'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ username: '', email: '', password: '', role: 'editor' });
                  }}
                  className="flex-1 bg-gray-100 hover:bg-gray-100 px-4 py-2 rounded-lg transition"
                >
                  キャンセル
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 編集モーダル */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-white/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-amber-500 mb-4">ユーザー編集</h3>
            <form onSubmit={handleUpdateUser}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">ユーザー名</label>
                  <input
                    type="text"
                    disabled
                    value={formData.username}
                    className="w-full bg-gray-100/50 border border-gray-300 rounded-lg px-4 py-2 opacity-50"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">メールアドレス</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg px-4 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    新しいパスワード（変更する場合のみ）
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg px-4 py-2"
                    placeholder="変更しない場合は空欄"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">権限</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg px-4 py-2"
                    disabled={selectedUser.username === 'admin'}
                  >
                    <option value="admin">管理者</option>
                    <option value="editor">編集者</option>
                    <option value="viewer">閲覧者</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 px-4 py-2 rounded-lg transition disabled:opacity-50"
                >
                  {loading ? '更新中...' : '更新'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                    setFormData({ username: '', email: '', password: '', role: 'editor' });
                  }}
                  className="flex-1 bg-gray-100 hover:bg-gray-100 px-4 py-2 rounded-lg transition"
                >
                  キャンセル
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
